import React, { createContext, useContext, useState, useEffect } from "react";
import toast from "react-hot-toast";
import apiClient from "../services/api/client";
import ENDPOINTS from "../services/api/endpoints";

const AuthContext = createContext();

/**
 * Default enabled modules — fallback when API is unavailable.
 * Codes match the DB module.code column (uppercase).
 */
const DEFAULT_ENABLED_MODULES = [
  'POS', 'KDS', 'BACKOFFICE', 'INVENTORY',
  'REPORTS', 'CRM', 'HRM', 'AI_INSIGHTS',
  'INTEGRATIONS', 'CAPTAIN_APP', 'QR_MENU'
];

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);


  useEffect(() => {
    const storedUser = sessionStorage.getItem("restaurant_user");
    const storedToken = localStorage.getItem("token");

    if (storedUser && storedToken) {
      try {
        const parsedUser = JSON.parse(storedUser);
        
        // Use stored modules from API; fall back to defaults if missing
        if (!parsedUser.enabledModules || parsedUser.enabledModules.length === 0) {
          parsedUser.enabledModules = DEFAULT_ENABLED_MODULES;
          sessionStorage.setItem("restaurant_user", JSON.stringify(parsedUser));
        }
        setUser(parsedUser);
      } catch (error) {
        console.error("Error parsing stored user:", error);
        sessionStorage.removeItem("restaurant_user");
        localStorage.removeItem("token");
      }
    }
    setLoading(false);
  }, []);


  const login = async (username, password) => {
    try {
      if (!username || !password) {
        toast.error("Please enter username and password");
        return false;
      }
      const response = await apiClient.post(ENDPOINTS.AUTH.LOGIN, { username, password });

      // Note: apiClient response interceptor already unwraps response.data
      const { username: backendUsername, token, tokenType, expiryDate } = response;

      if (!token) {
        toast.error("Login failed: No token received");
        return false;
      }



      // Decode JWT payload to extract user details (handle base64url encoding)
      let firstName = '';
      let lastName = '';
      let branchId = null;
      let userType = null;
      try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(atob(base64));
        firstName = payload.firstname || '';
        lastName = payload.lastname || '';
        branchId = payload.branchIds || null;
        userType = payload.userType || null;
      } catch (e) {
        console.warn('Failed to decode JWT payload', e);
      }

      const fullName = [firstName, lastName].filter(Boolean).join(' ') || backendUsername;

      const role =
        backendUsername.toLowerCase().includes("admin")
          ? "admin"
          : backendUsername.toLowerCase().includes("pos")
            ? "pos"
            : "both";


      // Store token first so apiClient can use it for subsequent calls
      localStorage.setItem("token", token);

      // Fetch subscribed modules from API
      let enabledModules = DEFAULT_ENABLED_MODULES;
      try {
        const modulesResponse = await apiClient.get(ENDPOINTS.MODULES.ENABLED);
        if (Array.isArray(modulesResponse) && modulesResponse.length > 0) {
          enabledModules = modulesResponse;
        }
      } catch (err) {
        console.warn('Failed to fetch enabled modules, using defaults', err);
      }

     // Fetch user permissions + discount threshold from session
               let permissions = [];
               let discountThreshold = null;
              // console.log("Manual permissions:", permissions);
           try {
             const sessionResponse = await apiClient.get(ENDPOINTS.AUTH.SESSION);
             if (sessionResponse?.permissions) {
               permissions = Array.isArray(sessionResponse.permissions)
                 ? sessionResponse.permissions
                 : [...sessionResponse.permissions];
             }
             // Capture per-user discount cap (top-level field on session response,
             // e.g. 20.00 meaning 20% maximum manual discount this user may apply).
             if (sessionResponse?.discountThreshold !== undefined
                 && sessionResponse?.discountThreshold !== null) {
               const n = Number(sessionResponse.discountThreshold);
               if (!Number.isNaN(n)) discountThreshold = n;
             }
           } catch (err) {
             console.warn('Failed to fetch session permissions', err);
           }

           const userData = {
             username: backendUsername,
             name: fullName,
             firstName,
             lastName,
             branchId,
             userType,
             tokenType: tokenType?.trim() || "Bearer",
             expiryDate,
             role,
             enabledModules,
             permissions,
             discountThreshold,
             subscription: {
               plan: 'enterprise',
               expiresAt: null,
             },
           };

      sessionStorage.setItem("restaurant_user", JSON.stringify(userData));

      setUser(userData);
      toast.success(`Welcome back, ${fullName}!`);
      return true;
    } catch (error) {
      console.error("Login error:", error);
      if (error.response?.status === 401) {
        toast.error("Invalid username or password");
      } else {
        toast.error("Login failed. Please try again.");
      }
      return false;
    }
  };

  const logout = () => {
    setUser(null);
    sessionStorage.removeItem("restaurant_user");
    localStorage.removeItem("token");
    toast.success("Logged out successfully");
  };

  const isAuthenticated = () => !!user;
  const hasRole = (role) => user && (user.role === role || user.role === "both");

  /**
   * Check if user has access to a specific module
   * @param {string} moduleId - The module ID to check (e.g., 'pos', 'inventory', 'crm')
   * @returns {boolean} - True if user has access, false otherwise
   *
   * Future: This will check against subscription-based module access from API
   * Current: Returns true for all modules in DEFAULT_ENABLED_MODULES
   */
  const hasModuleAccess = (moduleId) => {
    // If no user or no enabledModules, allow all (development fallback)
    if (!user?.enabledModules) return true;
    return user.enabledModules.includes(moduleId);
  };

  /**
   * Get user's subscription info
   * @returns {object} - Subscription details { plan, expiresAt }
   */
  const getSubscription = () => {
    return user?.subscription || { plan: 'basic', expiresAt: null };
  };

  /**
   * Get list of enabled modules for current user
   * @returns {string[]} - Array of enabled module IDs
   */
  const getEnabledModules = () => {
    return user?.enabledModules || DEFAULT_ENABLED_MODULES;
  };

  /**
   * Check if user has a specific permission
   * @param {string} code - Permission code (e.g. 'BACKOFFICE.MENU.ITEM.ADD')
   * @returns {boolean}
   */
 const hasPermission = (perm) => {
  // SUPER ADMIN → full access
  if (user?.userType === "SUPER_USER") return true;

  if (!user || !Array.isArray(user.permissions) || user.permissions.length === 0) {
    return false;
  }

  if (!perm) return false;

  return user.permissions.some((p) => {
    if (!p) return false;

    const pLower = String(p).toLowerCase();
    const permLower = String(perm).toLowerCase();

    return (
      pLower === permLower ||
      pLower.startsWith(permLower + ".")
    );
  });
};

  /**
   * Check if user has at least one of the given permissions
   * @param {string[]} codes - Array of permission codes
   * @returns {boolean}
   */
  const hasAnyPermission = (codes) => {
    if (!user) return false;
    if (user.userType === 'SUPER_USER') return true;
    if (!user.permissions || !Array.isArray(codes)) return false;
    return codes.some((code) => user.permissions.includes(code));
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        isAuthenticated,
        hasRole,
        hasModuleAccess,
        getSubscription,
        getEnabledModules,
        hasPermission,
        hasAnyPermission,
        loading
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};